<template>
  <DefaultLayoutComponent>
    <div class="homepage">
      <HomepageHero
        class="homepage__hero"
        @show-more="handleShowMore"
        @show-pricing="startOrder({ 'package-id': 2 })"
        @start-demo="handleStartDemo"
      />

      <KnownFrom class="homepage__known-from mobile" />

      <!--
        Below-the-fold sections use Nuxt delayed hydration (hydrate-on-visible): they are still
        fully server-rendered (content + SEO + CTAs intact and clickable as real links), their JS
        is code-split out of the initial bundle, and hydration runs only as each section scrolls
        into view. This cuts initial main-thread work (the long task / TBT that PageSpeed flagged)
        without delaying the hero CTA. The hero and the known-from logos stay eager.
      -->
      <LazyGeneralUspsBlock
        class="homepage__general-usps"
        hydrate-on-visible
      />

      <LazyHowItWorksFeatureGrid
        class="homepage__feature-grid"
        hydrate-on-visible
      />

      <LazyCustomerServiceCtaBanner
        class="homepage__cta-banner"
        hydrate-on-visible
      />

      <!--    <floating-demo-cta class="homepage__demo-cta" />-->

      <LazyPriceMatrix
        class="homepage__price-matrix"
        hydrate-on-visible
      />

      <AppWave
        primary-color
        :show-bottom="false"
        class="homepage__reviews-wave"
      >
        <LazyReviewsBlock
          id="reviews"
          class="homepage__reviews-block"
          hydrate-on-visible
        />
      </AppWave>
    </div>
  </DefaultLayoutComponent>
</template>

<script setup>
  const router = useRouter()
  const { t: $t } = useI18n()
  const { howItWorksLink, startOrder, eventDemoOrder } = useLinks()

  function handleShowMore() {
    router.push(howItWorksLink.value)
  }

  function handleStartDemo() {
    router.push(eventDemoOrder.value)
  }

  const pageTitle = computed(() => $t('seo.home_page.title'))
  const pageDescription = computed(() => $t('seo.home_page.description'))

  useHead({
    title: pageTitle,
    meta: [{ name: 'description', content: pageDescription }],
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          '@id': 'https://momentshare.io/#software',
          'name': 'Momentshare',
          'url': 'https://momentshare.io',
          'applicationCategory': 'MultimediaApplication',
          'applicationSubCategory': 'Photo Sharing',
          'operatingSystem': 'Web, iOS, Android',
          'description':
            'Collect and share photos and videos from your event using a QR code. Works in any browser — optimised native apps available for iOS and Android.',
          'sameAs': [
            'https://apps.apple.com/nl/app/momentshare/id6762079606',
            'https://play.google.com/store/apps/details?id=io.momentshare.momentshare_event_app',
          ],
          'offers': [
            {
              '@type': 'Offer',
              'name': 'Starter',
              'price': 19,
              'priceCurrency': 'EUR',
              'description':
                'For birthdays, family days and smaller weddings, hosted by Momentshare. Up to 100MB per file, valid for 1 month',
            },
            {
              '@type': 'Offer',
              'name': 'Premium',
              'price': 39,
              'priceCurrency': 'EUR',
              'description':
                'For weddings, anniversary parties and events with many guests. Live slideshow and casting to your TV, 1000MB per file, valid for 6 months',
            },
            {
              '@type': 'Offer',
              'name': 'Unlimited',
              'price': 49,
              'priceCurrency': 'EUR',
              'description': 'Unlimited video size, no restrictions on uploads',
            },
          ],
          'aggregateRating': {
            '@type': 'AggregateRating',
            'ratingValue': 4.7,
            'ratingCount': 82,
            'bestRating': 5,
            'worstRating': 1,
          },
          'publisher': { '@id': 'https://momentshare.io/#organization' },
        }),
      },
    ],
  })
</script>

<style lang="scss">
  .homepage__hero {
    width: 100%;
    max-width: var(--max-width-content);
    padding: 0 var(--content-padding);
    margin: 24px auto 0;

    @include breakpoint(desktop) {
      margin: 88px auto;
    }
  }

  // Mobile only: on desktop the logos sit inside the hero image column.
  .homepage__known-from {
    width: calc(100% - (var(--content-padding) * 2));
    max-width: var(--max-width-content);
    margin: 0 auto;
  }

  .homepage__general-usps {
    width: 100%;
    max-width: var(--max-width-content);
    margin: 72px auto 0;
    padding: 0 var(--content-padding);
  }

  .homepage__feature-grid {
    margin: 72px 0 0;

    @include breakpoint(desktop) {
      margin: 96px 0 0;
    }
  }

  .homepage__cta-banner {
    width: calc(100% - (var(--content-padding) * 2));
    max-width: var(--max-width-content);
    margin: 72px var(--content-padding) 0;

    @include breakpoint(desktop) {
      max-width: 1062px;
      margin: 72px auto 0;
    }
  }

  .homepage__price-matrix {
    max-width: var(--max-width-content);
    margin: 72px auto 0;
    padding: 0 var(--content-padding);

    @include breakpoint(desktop) {
      margin: 124px auto 0;
    }
  }

  .homepage__how-it-works {
    background: var(--color-primary);
  }

  .homepage__how-it-works-wave {
    margin: 64px 0 -42px;
    z-index: 1;

    @include breakpoint(desktop) {
      margin: 0;
    }
  }

  .homepage__demo-cta.floating-demo-cta {
    @media screen and (max-width: 1023px) {
      padding: 48px 16px 24px;
    }
  }

  .homepage__usps {
    display: flex;
    flex-direction: column;
    padding: 0 var(--spacing-small) 100px;
    gap: 48px;

    @include breakpoint(sm) {
      padding: 0 var(--spacing-default) 100px var(--spacing-default);
    }
  }

  .homepage__help-faq {
    margin-bottom: var(--spacing-larger);
  }

  .homepage__help-faq-title {
    margin-top: var(--spacing-large);
  }

  .homepage__reviews-wave {
    margin: 48px 0 0;

    @include breakpoint(desktop) {
      margin: 96px 0 0;
    }
  }

  .homepage__reviews-block {
    max-width: var(--max-width-content);
    margin: 0 auto;

    @include breakpoint(desktop) {
      padding: 0 var(--content-padding);
    }
  }

  .homepage__reviews-block .reviews-block__header,
  .homepage__reviews-block .reviews-block__wrapper {
    padding: 0 var(--content-padding);

    @include breakpoint(desktop) {
      padding: 0;
    }
  }

  .homepage__reviews-block .reviews-block__wrapper {
    border-bottom: 1px solid rgba(255, 255, 255, 0.5);
    padding-bottom: 36px;

    @include breakpoint(desktop) {
      padding-bottom: 60px;
    }
  }
</style>
